/**
 * Generic Court Search Page Component
 * Handles the common structure and logic for all search types (Cases, Opinions, Oral Arguments)
 */

import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import { Box } from '@ltvco/refresh-lib/theme';
import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import {
  ORAL_ARGUMENTS_FAQ_ITEMS,
  ORAL_ARGUMENTS_SORT_OPTIONS,
  CLASS_ACTION_LAWSUIT_FAQ_ITEMS,
  JUDGE_SEARCH_FAQ_ITEMS,
  JUDGE_SEARCH_SORT_OPTIONS,
} from '../../../constants';
import { usePagination } from '../../../utils';
import {
  FAQ_DISCLAIMER,
  HELPFUL_TIPS_V2,
  FAQ_ITEMS,
  SORT_OPTIONS,
  CLASS_ACTION_SORT_OPTIONS,
} from '../../constants';
import {
  type OpinionsResponse,
  type PACERResponse,
  type OralArgumentsResponse,
  type JudgeSearchResponse,
  type ClassActionSearchResponse,
  type ClassActionSearchCard,
} from '../../types';
import { sortClassActionCardsByFiledOn } from '../../utils';
import {
  useCourtSearchPage,
  useCasesSearch,
  useOpinionsSearch,
  useOralArgumentsSearch,
  useJudgeSearch,
  useClassActionLawsuitSearch,
} from '../hooks';
import {
  ResultsContainer,
  ErrorMessage,
  LoadingState,
  NoResults,
  PaginationControls,
  PaginationOverlay,
  HelpfulTips,
  FaqSection,
  SearchResultsHeader,
  SearchFiltersBar,
  MobileHeader,
  type ViewType,
} from '../index';

type SearchType =
  | 'cases'
  | 'opinions'
  | 'oral-arguments'
  | 'judge-search'
  | 'class-action-lawsuits';
type NoResultsType = SearchType | 'general';

type CasesSearchHookResult = ReturnType<typeof useCasesSearch>;
type OpinionsSearchHookResult = ReturnType<typeof useOpinionsSearch>;
type OralArgumentsSearchHookResult = ReturnType<typeof useOralArgumentsSearch>;
type JudgeSearchHookResult = ReturnType<typeof useJudgeSearch>;
type ClassActionLawsuitSearchHookResult = ReturnType<
  typeof useClassActionLawsuitSearch
>;
type AnyHookResult =
  | CasesSearchHookResult
  | OpinionsSearchHookResult
  | OralArgumentsSearchHookResult
  | JudgeSearchHookResult
  | ClassActionLawsuitSearchHookResult;

export interface SearchHookResult<
  TResponse extends
    | OpinionsResponse
    | PACERResponse
    | OralArgumentsResponse
    | JudgeSearchResponse
    | ClassActionSearchResponse
> {
  results: TResponse | null;
  searchQuery: string;
  sortOption: string;
  setSortOption: (value: string) => void;
  triggerCombinedSearch: (
    query: string,
    filters: Record<string, unknown>
  ) => void;
  isSearching: boolean;
  isAnyLoading: boolean;
  hasResults: boolean;
  generalError: string | null;
  isTokenLoading: boolean;
  updateResults: (results: TResponse) => void;
  setPaginationLoading: (loading: boolean) => void;
}

export interface CourtSearchPageProps<
  TResponse extends
    | OpinionsResponse
    | PACERResponse
    | OralArgumentsResponse
    | JudgeSearchResponse
    | ClassActionSearchResponse,
  TFormData extends {
    searchQuery: string;
    exactMatchOnly: boolean;
    filters: unknown;
  },
  TSearchType extends string
> {
  queryPrefixMap: Record<string, TSearchType>;
  normalizeFilters: (filters: unknown) => Record<string, unknown>;
  useSearchHook: (query: string, exactMatch: boolean) => AnyHookResult;
  initialQuery: string;
  initialExactMatch: boolean;
  SearchForm: React.ComponentType<{
    onSubmit?: (data: TFormData) => void;
    initialSearchType?: TSearchType;
    initialQuery?: string;
    initialExactMatch?: boolean;
    sortValue?: string;
    isLoading?: boolean;
    disabled?: boolean;
  }>;
  ResultsComponent: React.ComponentType<{
    results: TResponse extends { results: infer R } ? R : never;
    viewType?: ViewType;
  }>;
  /** Replaces default HelpfulTips when there are no results and a search is not in flight */
  emptyStateBelowSearchForm?: React.ReactNode;
}

const SEARCH_TYPE_CONFIG = {
  cases: {
    urlPath: '/cases',
    scrollElementId: 'court-cases-results',
    documentTitleBase: 'Court Cases',
    faqItems: FAQ_ITEMS,
    sortOptions: SORT_OPTIONS,
    noResultsType: 'cases' as const,
  },
  opinions: {
    urlPath: '/opinions',
    scrollElementId: 'court-opinions-results',
    documentTitleBase: 'Court Opinions',
    faqItems: FAQ_ITEMS,
    sortOptions: SORT_OPTIONS,
    noResultsType: 'opinions' as const,
  },
  'oral-arguments': {
    urlPath: '/oral-arguments',
    scrollElementId: 'oral-arguments-results',
    documentTitleBase: 'Oral Arguments',
    faqItems: ORAL_ARGUMENTS_FAQ_ITEMS,
    sortOptions: ORAL_ARGUMENTS_SORT_OPTIONS,
    noResultsType: 'oral-arguments' as const,
  },
  'judge-search': {
    urlPath: '/judge-search',
    scrollElementId: 'judge-search-results',
    documentTitleBase: 'Judge Search',
    faqItems: JUDGE_SEARCH_FAQ_ITEMS,
    sortOptions: JUDGE_SEARCH_SORT_OPTIONS,
    noResultsType: 'judge-search' as const,
  },
  'class-action-lawsuits': {
    urlPath: '/class-action-lawsuits',
    scrollElementId: 'class-action-lawsuits-results',
    documentTitleBase: 'Class Action Lawsuits',
    faqItems: CLASS_ACTION_LAWSUIT_FAQ_ITEMS,
    sortOptions: CLASS_ACTION_SORT_OPTIONS,
    noResultsType: 'class-action-lawsuits' as const,
  },
} as const;

const URL_PATH_TO_SEARCH_TYPE: Record<string, SearchType> = {
  '/class-action-lawsuits': 'class-action-lawsuits',
  '/cases': 'cases',
  '/opinions': 'opinions',
  '/oral-arguments': 'oral-arguments',
  '/judge-search': 'judge-search',
};

const HOOK_RESULT_MAPPERS = {
  cases: {
    getResults: (hookResult: CasesSearchHookResult): PACERResponse | null =>
      hookResult.pacerResults,
    getUpdateResults: (hookResult: CasesSearchHookResult) =>
      hookResult.updatePacerResults,
  },
  opinions: {
    getResults: (
      hookResult: OpinionsSearchHookResult
    ): OpinionsResponse | null => hookResult.opinionsResults,
    getUpdateResults: (hookResult: OpinionsSearchHookResult) =>
      hookResult.updateOpinionsResults,
  },
  'oral-arguments': {
    getResults: (
      hookResult: OralArgumentsSearchHookResult
    ): OralArgumentsResponse | null => hookResult.oralArgumentsResults,
    getUpdateResults: (hookResult: OralArgumentsSearchHookResult) =>
      hookResult.updateOralArgumentsResults,
  },
  'judge-search': {
    getResults: (
      hookResult: JudgeSearchHookResult
    ): JudgeSearchResponse | null => hookResult.judgeSearchResults,
    getUpdateResults: (hookResult: JudgeSearchHookResult) =>
      hookResult.updateJudgeSearchResults,
  },
  'class-action-lawsuits': {
    getResults: (
      hookResult: ClassActionLawsuitSearchHookResult
    ): ClassActionSearchResponse | null => hookResult.classActionLawsuitResults,
    getUpdateResults: (hookResult: ClassActionLawsuitSearchHookResult) =>
      hookResult.updateClassActionLawsuitResults,
  },
} as const;

const HOOK_PROPERTY_TO_TYPE: Record<string, keyof typeof HOOK_RESULT_MAPPERS> =
  {
    pacerResults: 'cases',
    opinionsResults: 'opinions',
    oralArgumentsResults: 'oral-arguments',
    judgeSearchResults: 'judge-search',
    classActionLawsuitResults: 'class-action-lawsuits',
  };

const getSearchTypeFromUrl = (pathname: string): SearchType | null => {
  const matchedPath = Object.keys(URL_PATH_TO_SEARCH_TYPE).find((path) =>
    pathname.includes(path)
  );
  return matchedPath ? URL_PATH_TO_SEARCH_TYPE[matchedPath] : null;
};

const getNoResultsTypeFromUrl = (pathname: string): NoResultsType => {
  const searchType = getSearchTypeFromUrl(pathname);
  return searchType ? SEARCH_TYPE_CONFIG[searchType].noResultsType : 'general';
};

const getHookMapper = (hookResult: AnyHookResult) => {
  const detectedProperty = Object.keys(HOOK_PROPERTY_TO_TYPE).find(
    (prop) => prop in hookResult
  ) as keyof typeof HOOK_PROPERTY_TO_TYPE | undefined;

  if (!detectedProperty) {
    throw new Error('Unable to detect hook result type');
  }

  const detectedType = HOOK_PROPERTY_TO_TYPE[detectedProperty];

  return {
    type: detectedType,
    mapper: HOOK_RESULT_MAPPERS[detectedType],
    hookResult: hookResult as
      | CasesSearchHookResult
      | OpinionsSearchHookResult
      | OralArgumentsSearchHookResult
      | JudgeSearchHookResult
      | ClassActionLawsuitSearchHookResult,
  };
};

const extractCommonHookProperties = (hookResult: AnyHookResult) => ({
  searchQuery: hookResult.searchQuery,
  sortOption: hookResult.sortOption,
  setSortOption: hookResult.setSortOption,
  triggerCombinedSearch: hookResult.triggerCombinedSearch,
  isSearching: hookResult.isSearching,
  isAnyLoading: hookResult.isAnyLoading,
  hasResults: hookResult.hasResults,
  generalError: hookResult.generalError,
  isTokenLoading: hookResult.isTokenLoading,
  setPaginationLoading: hookResult.setPaginationLoading,
});

const mapHookResultToSearchHookResult = <
  TResponse extends
    | OpinionsResponse
    | PACERResponse
    | OralArgumentsResponse
    | JudgeSearchResponse
    | ClassActionSearchResponse
>(
  hookResult: AnyHookResult
): SearchHookResult<TResponse> => {
  const { mapper, hookResult: typedHookResult } = getHookMapper(hookResult);
  const commonProps = extractCommonHookProperties(hookResult);

  return {
    ...commonProps,
    results: mapper.getResults(typedHookResult as any) as TResponse | null,
    updateResults: mapper.getUpdateResults(typedHookResult as any) as (
      results: TResponse
    ) => void,
  };
};

function CourtSearchPageInner<
  TResponse extends
    | OpinionsResponse
    | PACERResponse
    | OralArgumentsResponse
    | JudgeSearchResponse
    | ClassActionSearchResponse,
  TFormData extends {
    searchQuery: string;
    exactMatchOnly: boolean;
    filters: unknown;
  },
  TSearchType extends string
>({
  searchType,
  noResultsType,
  queryPrefixMap,
  normalizeFilters,
  useSearchHook,
  initialQuery,
  initialExactMatch,
  SearchForm,
  ResultsComponent,
  emptyStateBelowSearchForm,
}: CourtSearchPageProps<TResponse, TFormData, TSearchType> & {
  searchType: SearchType;
  noResultsType: NoResultsType;
}) {
  // All hooks called unconditionally (searchType is always valid here)
  const rawHookResult = useSearchHook(initialQuery, initialExactMatch);
  const searchHookResult =
    mapHookResultToSearchHookResult<TResponse>(rawHookResult);

  const {
    results,
    searchQuery,
    sortOption,
    setSortOption,
    triggerCombinedSearch,
    isSearching,
    isAnyLoading,
    hasResults,
    generalError,
    isTokenLoading,
    updateResults,
    setPaginationLoading,
  } = searchHookResult;

  const { data: appConstantsData } = useAppConstantsContext();
  const searchConfig = SEARCH_TYPE_CONFIG[searchType];
  const scrollElementId = searchConfig.scrollElementId;
  const faqItems = searchConfig.faqItems;
  const sortOptions = searchConfig.sortOptions;
  const documentTitle = `${searchConfig.documentTitleBase} - ${appConstantsData.brandName}`;

  const pageHook = useCourtSearchPage<TSearchType, TFormData>(
    {
      queryPrefixMap,
      defaultSearchType: 'keyword' as TSearchType,
      documentTitle,
      scrollElementId,
      searchQuery,
      exactMatch: initialExactMatch,
      sortOption,
      setSortOption,
      triggerCombinedSearch,
      currentSearchQuery: initialQuery,
      setCurrentSearchQuery: () => {},
      hasResults,
      isSearching,
      refetchOnSortChange: searchType !== 'class-action-lawsuits',
    },
    normalizeFilters
  );

  const {
    currentSearchQuery,
    exactMatch,
    currentSearchType,
    currentView,
    appliedFilters,
    handleSearchSubmit,
    handleViewChange,
    handleSortChange,
    handleExactMatchChange,
  } = pageHook;

  const { isLoadingPage, handlePreviousPage, handleNextPage } =
    usePagination<TResponse>({
      onPageChange: updateResults,
      onPaginationLoading: setPaginationLoading,
      endpoint:
        searchType === 'class-action-lawsuits'
          ? 'class-action-search'
          : 'search',
    });

  const classActionSortedCards = useMemo(() => {
    if (searchType !== 'class-action-lawsuits' || !results?.results?.length) {
      return null;
    }
    return sortClassActionCardsByFiledOn(
      results.results as ClassActionSearchCard[],
      sortOption
    );
  }, [results, searchType, sortOption]);

  return (
    <>
      <MobileHeader />

      <SearchForm
        onSubmit={handleSearchSubmit}
        isLoading={isSearching}
        initialSearchType={currentSearchType}
        initialQuery={currentSearchQuery}
        initialExactMatch={exactMatch}
        sortValue={sortOption}
        disabled={isTokenLoading}
      />

      {!hasResults &&
        !isSearching &&
        (emptyStateBelowSearchForm || HELPFUL_TIPS_V2) && (
          <Box sx={{ mt: 3, px: { xs: 2, sm: 0 } }}>
            {emptyStateBelowSearchForm ?? (
              <HelpfulTips tips={HELPFUL_TIPS_V2} />
            )}
          </Box>
        )}

      {hasResults && (
        <Box sx={{ mt: 4, mb: 3, px: { xs: 2, sm: 0 } }}>
          <SearchResultsHeader
            resultsCount={results?.count || 0}
            searchQuery={searchQuery}
            appliedFilters={appliedFilters}
            loading={isSearching}
            currentView={currentView}
            onViewChange={handleViewChange}
            showViewToggle={searchType !== 'class-action-lawsuits'}
            showAppliedFilters={searchType !== 'class-action-lawsuits'}
          />
        </Box>
      )}

      {hasResults && (
        <Box sx={{ mb: 3 }}>
          <SearchFiltersBar
            sortValue={sortOption}
            sortOptions={sortOptions}
            onSortChange={handleSortChange}
            exactMatch={exactMatch}
            onExactMatchChange={handleExactMatchChange}
          />
        </Box>
      )}

      {isSearching && (
        <Box sx={{ mt: 3 }} id={scrollElementId}>
          <LoadingState />
        </Box>
      )}

      {generalError && !isAnyLoading && (
        <ResultsContainer>
          <ErrorMessage message={generalError} />
        </ResultsContainer>
      )}

      {!isSearching && (hasResults || searchQuery) && (
        <ResultsContainer id={scrollElementId}>
          {results && results.count > 0 ? (
            <Box sx={{ position: 'relative' }}>
              <ResultsComponent
                results={
                  (classActionSortedCards ??
                    results.results) as TResponse extends { results: infer R }
                    ? R
                    : never
                }
                viewType={currentView}
              />

              <PaginationControls
                hasNext={!!results.next}
                hasPrevious={!!results.previous}
                isLoading={isLoadingPage}
                onNext={() => handleNextPage(results.next)}
                onPrevious={() => handlePreviousPage(results.previous)}
              />

              <PaginationOverlay isVisible={isLoadingPage} />
            </Box>
          ) : (
            <NoResults searchQuery={searchQuery} type={noResultsType} />
          )}
        </ResultsContainer>
      )}

      <Box sx={{ mt: { xs: 2.5, sm: 4 } }}>
        <FaqSection faqItems={faqItems} disclaimer={FAQ_DISCLAIMER} />
      </Box>
    </>
  );
}

export function CourtSearchPage<
  TResponse extends
    | OpinionsResponse
    | PACERResponse
    | OralArgumentsResponse
    | JudgeSearchResponse
    | ClassActionSearchResponse,
  TFormData extends {
    searchQuery: string;
    exactMatchOnly: boolean;
    filters: unknown;
  },
  TSearchType extends string
>(props: CourtSearchPageProps<TResponse, TFormData, TSearchType>) {
  const location = useLocation();
  const searchType = getSearchTypeFromUrl(location.pathname);
  const noResultsType = getNoResultsTypeFromUrl(location.pathname);

  if (!searchType) {
    return null; // Invalid search type
  }

  return (
    <CourtSearchPageInner
      {...props}
      searchType={searchType}
      noResultsType={noResultsType}
    />
  );
}
